import { version } from "../../../package.json";
import { castName, pimg } from "../art/cast";
import { drawEcg } from "../art/monitors";
import { ptext } from "../art/pixel";
import { titleScreen } from "../engine/flow";
import { bestGrade } from "../engine/progress";
import { prewarmSession } from "../engine/session";
import type { Department } from "../engine/types";
import { isEn, locale, resumingFromSwitch, switchLocale, t, tr, type Locale } from "../i18n";
import { $, h, isImeComposing, prefersReducedMotion } from "../lib/dom";
import { drawEmblem, emblemDataUrl, isBlinking } from "./emblem";
import { flipFrom, type SwapKind, windowIn, WindowMotion } from "./motion";
import { TitleScene } from "./scene";
import { setSfx, sfx, sfxEnabled } from "./sfx";

/**
 * Title screen in two phases, like a cabinet game:
 *   attract — night-hospital backdrop, logo, PRESS ANY KEY, PA ticker
 *   menu    — the department tabs and level list (rendered by engine/flow)
 * Everything here runs only while #title is on screen.
 */

type Phase = "attract" | "menu";

const title = $("#title");
const win = $("#titleWin");
const isVisible = (): boolean => title.style.display !== "none";

let phase: Phase = "attract";
let departments: readonly Department[] = [];
/** The title screen was on screen last frame (false while a level is running). */
let shown = false;
/** Set just before the menu window re-renders, so the motion knows which way to slide. */
let pendingSwap: { kind: SwapKind; dir: number } | null = null;

function setPhase(p: Phase): void {
  phase = p;
  title.dataset.phase = p;
}

/* ---------- static pieces ---------- */

/** Hospital PA announcements for the ticker. */
const PA = tr(
  [
    "院內廣播：婦產科值班 Clerk，請至三樓產房。",
    "請 3 號林怡君到產檢門診 2 診。",
    "急診檢傷一級，請相關人員注意。",
    "刀房 5 房：手術即將開始，請刷手上台。",
    "外科病房：明早七點查房，主任會問 lab。",
    "提醒各位見習同學：交班前請把病歷寫完。",
    "美食街營業到晚上九點。值班的你，已經錯過了。",
  ],
  [
    "Paging the OB/GYN clerk on call to Labor & Delivery, third floor.",
    "Number 3, Lin Yi-Chun, please come to prenatal clinic room 2.",
    "Triage level 1 in the ER. All responders, please stand by.",
    "OR 5: case about to start. Please scrub in.",
    "Surgical ward: rounds at 7 a.m. sharp. The attending will ask for labs.",
    "Reminder to all clerks: finish your notes before sign-out.",
    "The food court is open until 9 p.m. If you’re on call, you already missed it.",
  ],
);

function buildWordmark(): void {
  const letters = (word: string, offset: number): HTMLElement[] =>
    Array.from(word, (ch, i) => {
      const s = h("span", { text: ch });
      s.style.setProperty("--i", String(offset + i));
      return s;
    });
  $(".wordmark .w1").replaceChildren(...letters("CLERK", 0));
  $(".wordmark .w2").replaceChildren(...letters("SIMULATOR", 5));
}

function buildTicker(): void {
  const sep = tr("　◆　", "   ◆   ");
  const text = PA.join(sep) + sep;
  // Two copies side by side so the -50% scroll loops seamlessly.
  $("#marqueeText").replaceChildren(h("span", { text }), h("span", { text }));
  $("#marqueeText").style.setProperty("--dur", `${Math.round(text.length * 0.32)}s`);
}

function refreshHiGrade(): void {
  const levels = departments.flatMap((d) => d.levels);
  const counts: Record<string, number> = {};
  for (const lv of levels) {
    const g = bestGrade(lv.id);
    if (g) counts[g] = (counts[g] ?? 0) + 1;
  }
  const done = Object.values(counts).reduce((a, b) => a + b, 0);
  const parts = ["A", "B", "C", "D"].filter((g) => counts[g]).map((g) => `${g}×${counts[g] ?? 0}`);
  $("#hiGrade").textContent = done
    ? t("title.best", { grades: parts.join(" "), done, total: levels.length })
    : t("title.bestNone");
}

/* ---------- favicon blink ---------- */

// Every rel=icon link (SVG + PNG fallback): browsers differ in which one they show, so all of them blink.
const iconLinks = [...document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')].map((el) => ({
  el,
  href: el.getAttribute("href") ?? "",
  type: el.type,
}));
let iconUrls: [open: string, closed: string] | null = null;

function setTabIcon(closed: boolean | null): void {
  if (closed !== null) iconUrls ??= [emblemDataUrl(false), emblemDataUrl(true)];
  for (const { el, href, type } of iconLinks) {
    el.type = closed === null ? type : "image/png";
    el.href = closed === null || !iconUrls ? href : iconUrls[closed ? 1 : 0];
  }
}

/* ---------- start / menu ---------- */

let starting = false;

/** Acknowledge START with a rapid blink (like a cabinet), then bring the menu in. */
function pressStart(): void {
  if (phase !== "attract" || starting) return;
  starting = true;
  sfx.start();
  // Bot check + session cookie in the background, so the first conversation doesn't wait for it.
  prewarmSession();
  const ps = $("#pressStart");
  ps.classList.add("go");
  setTimeout(
    () => {
      ps.classList.remove("go");
      starting = false;
      enterMenu();
    },
    prefersReducedMotion() ? 0 : 320,
  );
}

function enterMenu(): void {
  const logo = $("#title .logo");
  const first = logo.getBoundingClientRect();
  pendingSwap = { kind: "enter", dir: 0 };
  setPhase("menu");
  titleScreen();
  flipFrom(logo, first);
  windowIn(win);
  focusFirst();
}

/** Work out how a click inside the menu window will change it, before flow re-renders it. */
function noteSwap(target: HTMLElement): void {
  const b = target.closest<HTMLElement>("#titleWin .btn");
  if (!b) return;
  if (b.classList.contains("tab")) {
    const tabs = [...win.querySelectorAll(".depts .tab")];
    const from = tabs.findIndex((t) => t.classList.contains("on"));
    const to = tabs.indexOf(b);
    if (to !== from) pendingSwap = { kind: "tab", dir: Math.sign(to - from) };
  } else if (b.classList.contains("lvcard")) pendingSwap = { kind: "forward", dir: 1 };
  else if (b.closest(".row") && !b.classList.contains("primary")) pendingSwap = { kind: "back", dir: -1 };
}

function initWindowMotion(): void {
  const motion = new WindowMotion(win);
  new MutationObserver(() => {
    // Not shown yet means we're coming back from a level: treat it as a fresh entrance.
    const swap = pendingSwap ?? { kind: shown ? "fade" : "enter", dir: 0 };
    pendingSwap = null;
    if (phase !== "menu") return;
    if (!shown) windowIn(win);
    motion.swapped(swap.kind, swap.dir);
  }).observe(win, { childList: true });
}

function focusFirst(): void {
  requestAnimationFrame(() => win.querySelector<HTMLElement>(".lvcard, .row .btn")?.focus({ preventScroll: true }));
}

/** ↑↓ move between cards / buttons, ←→ switch department, Esc backs out of a level intro. */
function menuKey(e: KeyboardEvent): void {
  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
    const items = [...win.querySelectorAll<HTMLElement>(".lvcard, .row .btn")];
    if (!items.length) return;
    const cur = items.indexOf(document.activeElement as HTMLElement);
    const step = e.key === "ArrowDown" ? 1 : -1;
    const next = items[cur < 0 ? 0 : (cur + step + items.length) % items.length];
    next?.focus();
    e.preventDefault();
  } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
    const tabs = [...win.querySelectorAll<HTMLButtonElement>(".depts .tab")];
    if (tabs.length < 2) return;
    const cur = tabs.findIndex((t) => t.classList.contains("on"));
    const step = e.key === "ArrowRight" ? 1 : -1;
    tabs[(cur + step + tabs.length) % tabs.length]?.click();
    focusFirst();
    e.preventDefault();
  } else if (e.key === "Escape") {
    const back = win.querySelector<HTMLButtonElement>(".row .btn:not(.primary)");
    if (back) {
      sfx.back();
      back.click();
      focusFirst();
    }
  }
}

/* ---------- easter egg ---------- */

const KONAMI = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "b",
  "a",
];
const EGG_LINES = tr(
  ["上上下下左右左右 BA……這招在 OSCE 沒有用。", "作弊碼？值班沒有無敵，只有交班。", "這麼閒的話，先去把病歷寫完。"],
  [
    "Up, up, down, down, left, right, left, right, B, A… That won’t get you through your OSCE.",
    "Cheat codes? There’s no invincibility on call. There’s only sign-out.",
    "If you have this much free time, go finish your notes.",
  ],
);
let konami = 0;
let eggTimer: ReturnType<typeof setTimeout> | undefined;

function easterEgg(): void {
  sfx.fanfare();
  const tabName = win.querySelector(".depts .tab.on")?.textContent;
  const dept = departments.find((d) => d.name === tabName) ?? departments[0];
  const who = dept?.attending ?? "vs";
  const line = EGG_LINES[Math.floor(Math.random() * EGG_LINES.length)] ?? "";
  const text = h("p");
  const box = $("#egg");
  box.replaceChildren(pimg(who), h("div", {}, h("b", { text: castName(who) }), text));
  box.hidden = false;
  let i = 0;
  const type = (): void => {
    i += isEn ? 2 : 1;
    text.textContent = line.slice(0, i);
    if (i < line.length && !box.hidden) setTimeout(type, 45);
  };
  type();
  title.classList.add("egg");
  if (!prefersReducedMotion())
    for (let n = 0; n < 26; n++) {
      const a = h("span", { className: "rain", text: "A" });
      a.style.left = `${Math.random() * 100}%`;
      a.style.animationDelay = `${Math.random() * 1.2}s`;
      a.style.animationDuration = `${1.6 + Math.random() * 1.2}s`;
      title.appendChild(a);
      setTimeout(() => a.remove(), 4200);
    }
  clearTimeout(eggTimer);
  eggTimer = setTimeout(() => {
    box.hidden = true;
    title.classList.remove("egg");
  }, 5200);
}

/* ---------- input & sound wiring ---------- */

let lastInputKeyboard = false;

function initInput(): void {
  document.addEventListener("keydown", (e) => {
    if (!isVisible() || isImeComposing(e)) return;
    if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
    // Enter / Space on the corner toggles is theirs, not PRESS ANY KEY.
    if (e.target instanceof HTMLElement && e.target.closest("#titleTools")) return;
    lastInputKeyboard = true;
    const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    konami = k === KONAMI[konami] ? konami + 1 : k === KONAMI[0] ? 1 : 0;
    if (konami === KONAMI.length) {
      konami = 0;
      easterEgg();
      e.preventDefault();
      return;
    }
    if (phase === "attract") {
      if (["Shift", "Control", "Alt", "Meta", "Tab", "CapsLock"].includes(e.key)) return;
      e.preventDefault();
      pressStart();
      return;
    }
    menuKey(e);
  });
  title.addEventListener("pointerdown", () => {
    lastInputKeyboard = false;
  });
  // Tap / click anywhere on the attract screen starts, like a cabinet's START button.
  // Capture phase: runs before the button's own onclick re-renders the window.
  title.addEventListener("click", (e) => noteSwap(e.target as HTMLElement), { capture: true });
  title.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (phase === "attract" && !target.closest("#titleTools")) {
      pressStart();
      return;
    }
    const b = target.closest<HTMLElement>(".btn");
    if (!b || b.closest("#titleTools")) return;
    if (b.classList.contains("tab")) sfx.move();
    else if (b.closest(".row") && !b.classList.contains("primary")) sfx.back();
    else sfx.select();
  });
  title.addEventListener("focusin", (e) => {
    if (lastInputKeyboard && (e.target as HTMLElement).classList.contains("btn")) sfx.move();
  });

  const btn = $<HTMLButtonElement>("#sfxBtn");
  const label = (): void => {
    btn.textContent = sfxEnabled() ? t("sfx.on") : t("sfx.off");
    btn.setAttribute("aria-pressed", String(sfxEnabled()));
  };
  btn.onclick = () => {
    setSfx(!sfxEnabled());
    label();
    sfx.select();
  };
  label();
  initLangSwitch();
}

function initLangSwitch(): void {
  for (const b of document.querySelectorAll<HTMLButtonElement>("#langSw .btn")) {
    const l = b.dataset.lang as Locale;
    b.setAttribute("aria-pressed", String(l === locale));
    b.onclick = () => {
      if (l === locale) return;
      sfx.select();
      switchLocale(l);
    };
  }
}

/* ---------- animation loop ---------- */

function startLoop(): void {
  const scene = new TitleScene($<HTMLCanvasElement>("#titleScene"));
  const foot = $("#titleFoot");
  const fit = (): void => scene.resize(foot.offsetHeight);
  fit();
  new ResizeObserver(fit).observe(foot);
  addEventListener("resize", fit);
  const emblem = $<HTMLCanvasElement>("#emblem").getContext("2d");
  const ecgCv = $<HTMLCanvasElement>("#titleEcg");
  const ecg = ecgCv.getContext("2d");
  let lastBlink: boolean | null = null;
  let lastFrame = 0;

  const frame = (now: number): void => {
    requestAnimationFrame(frame);
    const vis = isVisible();
    if (vis !== shown) {
      shown = vis;
      lastBlink = null;
      if (vis) refreshHiGrade();
      else setTabIcon(null);
    }
    if (!vis || now - lastFrame < 33) return;
    lastFrame = now;
    const still = prefersReducedMotion();
    scene.draw(now, still);
    const blink = !still && isBlinking(now);
    if (blink !== lastBlink) {
      lastBlink = blink;
      if (emblem) {
        emblem.clearRect(0, 0, 16, 16);
        drawEmblem(emblem, blink);
      }
      setTabIcon(blink);
    }
    if (ecg) {
      ecg.clearRect(0, 0, ecgCv.width, ecgCv.height);
      drawEcg(ecg, 0, 12, 96, 8, 72, still ? 0 : now, "#3be37a");
      ptext(ecg, "HR", 101, 3, "#3be37a");
      ptext(ecg, "72", 101, 11, "#3be37a");
    }
  };
  requestAnimationFrame(frame);
}

/** Build the title screen and show the attract phase. Call once at boot. */
export function initTitle(list: readonly Department[]): void {
  departments = list;
  buildWordmark();
  $("#titleTag").textContent = t("title.tag");
  $("#ver").textContent = `VER ${version}`;
  buildTicker();
  refreshHiGrade();
  initInput();
  initWindowMotion();
  $("#pressStart").onclick = pressStart;
  startLoop();
  if (resumingFromSwitch()) {
    // Just switched language: back to the menu the player was on, without the attract screen.
    prewarmSession();
    pendingSwap = { kind: "enter", dir: 0 };
    setPhase("menu");
    titleScreen();
    focusFirst();
  } else setPhase("attract");
}
